import { findCurrency } from '@/config/currencies';
import type { Adjustment, Money, QuoteItem, QuoteTotals } from '@/types';

/**
 * The single source of truth for money in QuotyLab.
 *
 * No component performs arithmetic on prices; they all read the object this
 * returns. That is what guarantees the number on screen, the number in the
 * totals card and the number on the PDF can never disagree.
 */

export interface PricingInput {
  items: readonly QuoteItem[];
  discount: Adjustment;
  taxRate: number;
  deposit: Adjustment;
  currency: string;
}

/** Half-up rounding that does not inherit binary floating-point drift. */
export function roundMoney(value: number, decimals: number): Money {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function lineTotal(item: Pick<QuoteItem, 'quantity' | 'unitPrice'>): Money {
  return Math.max(0, item.quantity) * Math.max(0, item.unitPrice);
}

/**
 * Resolves an adjustment against a base amount.
 * `percent` is relative; `fixed` is absolute and never exceeds the base.
 */
export function resolveAdjustment(adjustment: Adjustment, base: Money): Money {
  const value = Math.max(0, adjustment.value || 0);
  const amount = adjustment.mode === 'percent' ? (base * Math.min(value, 100)) / 100 : value;
  return Math.min(Math.max(0, amount), Math.max(0, base));
}

export function calculateTotals(input: PricingInput): QuoteTotals {
  const { decimals } = findCurrency(input.currency);
  const round = (value: number) => roundMoney(value, decimals);

  const subtotal = input.items.reduce((sum, item) => sum + lineTotal(item), 0);
  const taxableSubtotal = input.items
    .filter((item) => item.taxable)
    .reduce((sum, item) => sum + lineTotal(item), 0);

  const discountAmount = resolveAdjustment(input.discount, subtotal);

  // A discount reduces taxable and non-taxable lines in the same proportion,
  // which is how tax authorities expect a blended discount to be treated.
  const discountRatio = subtotal > 0 ? discountAmount / subtotal : 0;
  const taxableBase = taxableSubtotal * (1 - discountRatio);

  const taxRate = Math.min(Math.max(0, input.taxRate || 0), 100);
  const taxAmount = (taxableBase * taxRate) / 100;

  const total = subtotal - discountAmount + taxAmount;
  const depositAmount = resolveAdjustment(input.deposit, total);

  return {
    subtotal: round(subtotal),
    discountAmount: round(discountAmount),
    taxableBase: round(taxableBase),
    taxAmount: round(taxAmount),
    total: round(total),
    depositAmount: round(depositAmount),
    balanceDue: round(total - depositAmount),
  };
}
